import { SoftphoneSipConfig } from "../hooks/useSoftphoneConfig";
import { TranslateFn } from "../i18n/LocaleContext";

const ZERO_DATETIME = "0000-00-00 00:00:00";
const PAGE_SAFETY_CAP = 50;

export function normalizeRegistrarHost(registrar: string) {
  const sanitized = registrar.trim().replace(/^https?:\/\//i, "").replace(/\/+$/, "");
  return sanitized.split("/")[0] || "";
}

/**
 * Falha em uma requisição ao PABX. Carrega apenas o status HTTP: o corpo da resposta é
 * escrito pelo PABX e a URL da requisição leva o token na query string, então nada disso
 * pode chegar à tela do usuário.
 */
export class PabxRequestError extends Error {
  readonly status: number;

  constructor(status: number) {
    super(`PABX request failed with status ${status}`);
    this.name = "PabxRequestError";
    this.status = status;
  }
}

/** Origem + caminho, sem query string: é nela que vai o token, e isto vai para o log. */
export function describePabxUrl(url: string) {
  try {
    const parsed = new URL(url);
    return `${parsed.origin}${parsed.pathname}`;
  } catch {
    return "<url inválida>";
  }
}

const PROBE_TIMEOUT_MS = 5000;
const PROBE_CACHE_MS = 60_000;
const probeCache = new Map<string, { at: number; verdict: string }>();

// Para o JS, CORS bloqueado, DNS, TLS e rede fora do ar chegam todos como o mesmo TypeError
// ("Failed to fetch"), e a mensagem "blocked by CORS policy" é do navegador — não passa por
// console.*, então a captura remota nunca a vê. Um HEAD em `no-cors` não depende de CORS:
// resolve (resposta opaca) se o servidor respondeu, rejeita se não há como alcançá-lo. É o
// que separa "PABX sem Access-Control-Allow-Origin" de "PABX inalcançável". Sem query string
// (o token não é reenviado) e em cache por origem, porque a busca de contatos dispara uma
// requisição por termo digitado.
async function probePabxReachability(url: string): Promise<string> {
  let origin: string;
  let target: string;
  try {
    const parsed = new URL(url);
    origin = parsed.origin;
    target = `${parsed.origin}${parsed.pathname}`;
  } catch {
    return "URL inválida";
  }

  const cached = probeCache.get(origin);
  if (cached && Date.now() - cached.at < PROBE_CACHE_MS) return `${cached.verdict} (sonda em cache)`;

  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS);
  let verdict: string;
  try {
    await fetch(target, { method: "HEAD", mode: "no-cors", cache: "no-store", signal: controller.signal });
    verdict =
      "servidor alcançável, leitura bloqueada pelo navegador — provável ausência de " +
      "Access-Control-Allow-Origin na resposta do PABX (CORS)";
  } catch {
    verdict = controller.signal.aborted
      ? `servidor sem resposta em ${PROBE_TIMEOUT_MS} ms`
      : `servidor inalcançável (rede, DNS ou TLS) online=${navigator.onLine}`;
  } finally {
    window.clearTimeout(timer);
  }
  probeCache.set(origin, { at: Date.now(), verdict });
  return verdict;
}

/**
 * `fetch` das chamadas diretas do navegador ao PABX, com diagnóstico no log de console (que
 * a captura remota envia ao msteams-service quando `enableDebug` está ligado). Não altera o
 * comportamento: devolve a mesma Response e relança o mesmo erro. Só falhas são logadas;
 * cancelamentos (AbortController do chamador) não.
 */
export async function pabxFetch(operation: string, url: string, init: RequestInit = {}): Promise<Response> {
  const startedAt = performance.now();
  const target = describePabxUrl(url);
  try {
    const response = await fetch(url, init);
    if (!response.ok) {
      console.warn(
        `[pabx] ${operation} ${init.method || "GET"} ${target} -> HTTP ${response.status} ` +
          `em ${Math.round(performance.now() - startedAt)} ms`
      );
    }
    return response;
  } catch (error) {
    if (init.signal?.aborted) throw error;
    const elapsed = Math.round(performance.now() - startedAt);
    const name = error instanceof Error ? error.name : typeof error;
    const verdict = await probePabxReachability(url);
    console.error(`[pabx] ${operation} ${init.method || "GET"} ${target} falhou (${name}) em ${elapsed} ms: ${verdict}`);
    throw error;
  }
}

export type CallHistoryRecord = {
  id: string;
  direction: "in" | "out";
  counterpart: string;
  startedAt: Date | null;
  startRaw: string;
  durationSeconds: number;
  recordingUrl?: string;
};

function readString(value: unknown) {
  if (typeof value === "string") return value.trim();
  if (typeof value === "number") return String(value);
  return "";
}

function pad(value: number) {
  return String(value).padStart(2, "0");
}

function formatPabxDate(date: Date) {
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    ` ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
  );
}

function parsePabxDate(raw: string): Date | null {
  if (!raw || raw === ZERO_DATETIME) return null;
  const parsed = new Date(raw.replace(" ", "T"));
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function buildCallHistoryUrl(host: string, token: string, page: number) {
  const params = new URLSearchParams({ token, page: String(page) });
  return `https://${host}/utech/v1/call/callhistory?${params.toString()}`;
}

function mapCallRecord(
  id: string,
  raw: Record<string, unknown>,
  extension: string
): CallHistoryRecord | null {
  const source = readString(raw.source);
  const destination = readString(raw.destination);
  const startRaw = readString(raw.start);
  const durationSeconds = Number.parseInt(readString(raw.duration), 10) || 0;

  // Direção relativa ao ramal configurado: o campo "direction" costuma vir "-"
  // em chamadas internas, então deriva-se de source/destination.
  let direction: "in" | "out";
  if (extension && source === extension) {
    direction = "out";
  } else if (extension && destination === extension) {
    direction = "in";
  } else {
    direction = readString(raw.direction).toUpperCase() === "IN" ? "in" : "out";
  }
  const counterpart = direction === "out" ? destination : source;

  const recordRaw = readString(raw.record);
  const recordingUrl = /^https?:\/\//i.test(recordRaw) ? recordRaw : undefined;

  return {
    id,
    direction,
    counterpart,
    startedAt: parsePabxDate(startRaw),
    startRaw,
    durationSeconds,
    recordingUrl,
  };
}

/**
 * Busca o histórico de chamadas do ramal nos últimos `days` dias, paginando até o fim.
 */
export async function fetchCallHistory(
  sipConfig: SoftphoneSipConfig,
  token: string,
  t: TranslateFn,
  options: { days?: number; signal?: AbortSignal } = {}
): Promise<CallHistoryRecord[]> {
  const host = normalizeRegistrarHost(sipConfig.domain || "");
  const extension = (sipConfig.extension || "").trim();
  if (!host) throw new Error(t("pabxApi.error.sipDomainUnavailable"));
  if (!token) throw new Error(t("pabxApi.error.apiTokenUnavailable"));

  const days = options.days ?? 30;
  const dateEnd = new Date();
  const dateIni = new Date(dateEnd.getTime() - days * 24 * 60 * 60 * 1000);

  const body = {
    date_ini: formatPabxDate(dateIni),
    date_end: formatPabxDate(dateEnd),
    number: extension,
    substring: false,
    direction: "both",
    order: "datedesc",
  };

  const records: CallHistoryRecord[] = [];
  let fetchedCount = 0;

  for (let page = 0; page < PAGE_SAFETY_CAP; page += 1) {
    const response = await pabxFetch("callHistory", buildCallHistoryUrl(host, token, page), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      signal: options.signal,
    });

    if (!response.ok) {
      throw new PabxRequestError(response.status);
    }

    const payload = (await response.json()) as Record<string, unknown>;
    // Envelope: { status, calls: { list: { <id>: {...} }, total, count, page } }
    const calls =
      payload.calls && typeof payload.calls === "object" && !Array.isArray(payload.calls)
        ? (payload.calls as Record<string, unknown>)
        : {};
    const list =
      calls.list && typeof calls.list === "object" && !Array.isArray(calls.list)
        ? (calls.list as Record<string, unknown>)
        : {};
    const entries = Object.entries(list);

    for (const [id, value] of entries) {
      if (!value || typeof value !== "object") continue;
      const record = mapCallRecord(id, value as Record<string, unknown>, extension);
      if (record) records.push(record);
    }

    // count = chamadas retornadas nesta página; total = total filtrado.
    const count = Number.parseInt(readString(calls.count), 10);
    const total = Number.parseInt(readString(calls.total), 10);
    fetchedCount += Number.isFinite(count) ? count : entries.length;

    // Para quando a página não retornou nada ou já cobrimos o total filtrado.
    if (entries.length === 0 || (Number.isFinite(total) && fetchedCount >= total)) break;
  }

  records.sort((a, b) => {
    const aTime = a.startedAt ? a.startedAt.getTime() : 0;
    const bTime = b.startedAt ? b.startedAt.getTime() : 0;
    return bTime - aTime;
  });

  return records;
}
